// app/frontend/components/ui/Tickets/FundingDeadlineBadge.tsx
import { useEffect, useRef, useState } from 'react';
import { Group } from '@mantine/core';
import { IconClock } from '@tabler/icons-react';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { COLORS } from '../../../src/constants/theme';
import { themeAlpha, themeOnTint } from '../../../src/utils/pageTheme';

dayjs.extend(duration);

// Live countdown badge for a Fund the Floor deadline. Ticks every second,
// shifts from the page accent to red inside 24h to signal urgency.
export interface FundingDeadlineBadgeProps {
  expiryTimestamp: string;
  /** Called once when the countdown reaches zero, so the host can re-decide the goal. */
  onExpire?: () => void;
}

export default function FundingDeadlineBadge({ expiryTimestamp, onExpire }: FundingDeadlineBadgeProps) {
  const compute = () => {
    const diff = dayjs(expiryTimestamp).diff(dayjs());
    if (diff <= 0) return null;
    const d = dayjs.duration(diff);
    const urgent = diff < 24 * 60 * 60 * 1000;
    let text: string;
    if (d.days() > 0) text = `${d.days()}d ${d.hours()}h ${d.minutes()}m`;
    else if (d.hours() > 0) text = `${d.hours()}h ${d.minutes()}m ${d.seconds()}s`;
    else text = `${d.minutes()}m ${d.seconds()}s`;
    return { text, urgent };
  };
  const [state, setState] = useState(compute());
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;
  useEffect(() => {
    let fired = false;
    const tick = () => {
      const next = compute();
      setState(next);
      if (!next && !fired) {
        fired = true;
        clearInterval(id);
        onExpireRef.current?.();
      }
    };
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiryTimestamp]);
  if (!state) return null;
  const color = state.urgent ? COLORS.danger : themeOnTint();
  const bg = state.urgent ? 'rgba(239, 68, 68, 0.12)' : themeAlpha(0.15);
  const border = state.urgent ? 'rgba(239, 68, 68, 0.5)' : themeAlpha(0.45);
  return (
    <Group
      gap={4}
      wrap="nowrap"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: 999,
        background: bg,
        border: `1px solid ${border}`,
        color,
        fontWeight: 600,
        fontSize: 11,
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}
    >
      <IconClock size={11} />
      {state.text}
    </Group>
  );
}
